import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from "./TranslationContext";
import s from '../About.module.css';

/* Reveal on scroll */
const useReveal = (ref) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add(s.visible); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
};

export const About = () => {
  const navigate = useNavigate();
  const { t } = useAppTranslation();

  const [reviews, setReviews] = useState([]);

  const missionRef  = useRef(null);
  const featuresRef = useRef(null);
  const authorRef   = useRef(null);
  const reviewsRef  = useRef(null);

  useReveal(missionRef);
  useReveal(featuresRef);
  useReveal(authorRef);
  useReveal(reviewsRef);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await fetch("https://api.aiassessment.uz/api/comment-kurs/");
        if (!res.ok) throw new Error("Ошибка загрузки комментариев");
        const data = await res.json();
        const topReviews = data.filter(r => r.stars === 5).slice(0, 6);
        const reviewsWithUsers = await Promise.all(
          topReviews.map(async (rev) => {
            try {
              const userRes = await fetch(`https://api.aiassessment.uz/api/users/${rev.user}/`);
              if (!userRes.ok) throw new Error();
              const userData = await userRes.json();
              return {
                ...rev,
                userName: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username
              };
            } catch {
              return { ...rev, userName: "Пользователь" };
            }
          })
        );
        setReviews(reviewsWithUsers);
      } catch (err) {
        console.error(err);
      }
    };
    fetchReviews();
  }, []);

  return (
    <div>

      {/* ── HERO ── */}
      <div className={s.hero}>
        <video autoPlay loop muted playsInline className={s.heroBg}>
          <source src="/videos/about-bg.mp4" type="video/mp4" />
        </video>
        <div className={s.heroOverlay} />
        <div className={s.heroContent}>
          <span className={s.heroBadge}>{t('aboutTitle1')}</span>
          <h1 className={s.heroTitle}>
            {t('aboutTitle2')}
          </h1>
          <p className={s.heroSub}>{t('aboutSub')}</p>
          <button className={s.heroBtn} onClick={() => navigate('/course/2')}>
            <span className={s.heroBtnText}>{t('footerBtn')}</span>
          </button>
        </div>
      </div>

      {/* ── MISSION ── */}
      <section className={`${s.mission} ${s.reveal}`} ref={missionRef}>
        <div className={s.missionLeft}>
          <span className={s.missionLabel}>{t('mission')}</span>
          <h2 className={s.missionTitle}>{t('mission')}</h2>
          <img src="/images/about-aim2.png" alt="" className={s.missionImg} />
        </div>
        <p className={s.missionText}>{t('missionText')}</p>
      </section>

      {/* ── FEATURES / WHY US ── */}
      <section className={`${s.features} ${s.reveal}`} ref={featuresRef}>
        <div className={s.featuresInner}>
          <span className={s.featuresLabel}>{t('whyWe')}</span>
          <h2 className={s.featuresTitle}>{t('whyWe')}</h2>
          <ul className={s.featuresList}>
            {[
              { gif: '/images/1.gif', text: t('why1') },
              { gif: '/images/2.gif', text: t('why2') },
              { gif: '/images/3.gif', text: t('why3') },
              { gif: '/images/4.gif', text: t('why4') },
            ].map((item, i) => (
              <li
                key={i}
                className={s.featureItem}
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <span className={s.featureNum}>0{i + 1}</span>
                <img src={item.gif} alt="" className={s.featureIcon} />
                <p className={s.featureText}>{item.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── AUTHOR ── */}
      <section className={`${s.author} ${s.reveal}`} ref={authorRef}>
        <div className={s.authorInner}>
          <div className={s.authorImgWrap}>
            <img
              src="/images/author.jpg"
              alt="author"
              className={s.authorImg}
            />
          </div>
          <div className={s.authorContent}>
            <span className={s.authorLabel}>{t('author')}</span>
            <h2 className={s.authorName}>{t('authorName')}</h2>
            <span className={s.authorDeg}>{t('authoeDeg')}</span>
            <p className={s.authorDesc}>{t('authorDesc')}</p>
            <p className={s.authorDesc}>{t('authorDesc2')}</p>
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section className={`${s.reviews} ${s.reveal}`} ref={reviewsRef}>
        <div className={s.reviewsInner}>
          <div className={s.reviewsHeader}>
            <div>
              <span className={s.reviewsLabel}>{t('reviews')}</span>
              <h2 className={s.reviewsTitle}>{t('reviews')}</h2>
            </div>
          </div>

          {reviews.length > 0 ? (
            <div className={s.reviewsGrid}>
              {reviews.map((rev) => (
                <div key={rev.id} className={s.reviewCard}>
                  <span className={s.reviewCardQuote}>&ldquo;</span>
                  <p className={s.reviewText}>{rev.comment}</p>
                  <div className={s.reviewAuthor}>
                    <span className={s.reviewDash} />
                    {rev.userName}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className={s.reviewsLoading}>Загрузка отзывов...</p>
          )}
        </div>
      </section>

    </div>
  );
};
